import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { anchorTo, DropdownMenu, Icon, Tooltip, useAnchorRect } from '@forecastify/ui';
import { formatHours, parseHours, type ResourceItem } from '@forecastify/shared';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C53 · the planner's CELL, which is the unit the mockup works in.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS SCREEN PUT THE CELL'S CONTROLS ON THE ROW, AND SAID SO IN A COMMENT.** The note on
 * `onPatchItem` read: *"The mockup puts planned overtime and the distribution mode in a per-CELL
 * pop-over, which reads as `set overtime for this week` and sets it for the whole plan. In our
 * schema they are columns on the allocation, so the control that changes them belongs where that
 * is legible."* The reasoning is sound and the conclusion was wrong, for one reason the comment
 * could not see: the pop-over is not *a menu that happens to hang off a cell*. It is where a day's
 * bookings are ENUMERATED — `allocsOf(uid, li)` is a filter — and a person can hold two on one
 * deliverable with different overtime, different priority and different booking kinds. A row-level
 * menu cannot express that, because there is no single row to hang it on.
 *
 * ⚠️ **So the honest answer to *"whose property is this?"* is: the allocation's, and the cell is
 * how you reach the right one.** Each entry below says which booking it is editing (its span and
 * its whole total), so nothing pretends to be per-week that is not.
 *
 * ⚠️ **Hours ARE per column.** Typing in an entry rewrites that booking's share of this column
 * only, which is the one genuinely windowed thing here and why it sits beside the rest.
 */

export interface CellTarget {
  readonly memberId: string;
  readonly memberName: string;
  readonly lineItemId: string;
  readonly index: number;
}

export function AllocationCell({
  target,
  anchor,
  items,
  columnLabel,
  overtimeTypes,
  canManage,
  onHours,
  onPatch,
  onDelete,
  onClear,
  onAdd,
  onClose,
}: {
  readonly target: CellTarget;
  readonly anchor: RefObject<HTMLElement | null>;
  /** Every booking this person holds on this deliverable — one entry each. */
  readonly items: readonly ResourceItem[];
  readonly columnLabel: string;
  readonly overtimeTypes: readonly { id: string; name: string; multiplier: string }[];
  readonly canManage: boolean;
  readonly onHours: (item: ResourceItem, hours: number) => void;
  readonly onPatch: (
    item: ResourceItem,
    patch: {
      overtimeTypeId?: string | null;
      distribution?: 'even' | 'earliest';
      booking?: 'hard' | 'soft';
      /** 27 Oct 2026 · the column a distribution change re-spreads (`patchAllocationRequestSchema`). */
      fromDate?: string;
      toDate?: string;
    },
  ) => void;
  readonly onDelete: (item: ResourceItem) => void;
  /** 27 Oct 2026 · empty THIS column of a booking, leaving its other weeks alone. */
  readonly onClear: (item: ResourceItem) => void;
  readonly onAdd: () => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const { rect } = useAnchorRect(true, anchor);
  const surface = useRef<HTMLDivElement | null>(null);

  /**
   * ⚠️ **Escape closes and a click outside closes, and both are the same requirement.** A pop-over
   * that can only be dismissed by its own × traps a reader who opened the wrong cell — which, in a
   * grid of a hundred of them, is most of the time somebody opens one.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onDown = (event: MouseEvent): void => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      if (surface.current?.contains(node) === true) return;
      if (anchor.current?.contains(node) === true) return;
      /**
       * ⚠️⚠️ **A MENU OPENED FROM IN HERE IS NOT *OUTSIDE*, THOUGH THE DOM SAYS IT IS.**
       * `DropdownMenu` portals its surface into `document.body`, so it is nowhere near this one in
       * the tree — and `mousedown` runs BEFORE `click`. Without this, choosing an overtime type
       * closed the pop-over on the way down and the item's own handler never ran: every menu in
       * here silently did nothing. Found by a test, not by looking, because the pop-over shutting
       * is exactly what a reader expects to see after choosing something.
       */
      if (node instanceof Element && node.closest('[role="menu"]') !== null) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);

  if (typeof document === 'undefined') return null;

  const first = items[0];
  const total = items.reduce((sum, item) => sum + Number(item.cells[target.index] ?? '0'), 0);

  return createPortal(
    <div
      ref={surface}
      role="dialog"
      aria-label={t('resource.cell.title', {
        name: target.memberName,
        column: columnLabel,
      })}
      className="bg-popover text-popover-foreground shadow-pop border-border w-[320px] rounded-lg border p-3"
      style={anchorTo(rect, { side: 'bottom', align: 'start', width: 320 })}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-medium">{target.memberName}</div>
          <div className="text-muted-foreground truncate text-[11px]">
            {[first?.contractIdentifier, first?.lineItemName, columnLabel]
              .filter((one) => one !== null && one !== undefined && one !== '')
              .join(' · ')}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('common.close')}
          className="text-muted-foreground hover:text-foreground -mt-0.5 shrink-0"
          onClick={onClose}
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="mt-2 flex flex-col gap-1.5">
        {items.map((item, index) => (
          <Entry
            /* ⚠️ A draft has no id to key on — that is what makes it a draft — and two of them on
               one deliverable would otherwise collide. The position is stable for as long as the
               list is: it only reorders when the server answers, which replaces them anyway. */
            key={item.allocationId === '' ? `draft-${String(index)}` : item.allocationId}
            item={item}
            index={target.index}
            canManage={canManage}
            overtimeTypes={overtimeTypes}
            onHours={onHours}
            onPatch={onPatch}
            onDelete={onDelete}
            onClear={onClear}
            columnLabel={columnLabel}
          />
        ))}
      </div>

      {/**
       * ⚠️ **The total, and only when there are two of them.** With one booking it restates the
       * field directly above it, which is a figure that teaches the reader to stop reading.
       */}
      {items.length < 2 ? null : (
        <div className="text-muted-foreground mt-2 flex items-center justify-between border-t pt-2 text-[11px]">
          <span>{t('resource.cell.total')}</span>
          <span className="text-foreground font-medium tabular-nums">{formatHours(total)}</span>
        </div>
      )}

      {!canManage ? null : (
        <button
          type="button"
          className="text-primary hover:bg-accent mt-2 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-[11.5px]"
          onClick={onAdd}
        >
          <Icon name="circle-plus" size={13} />
          {t('resource.cell.add')}
        </button>
      )}
    </div>,
    document.body,
  );
}

/** One booking, with the three properties that belong to it and the hours that belong to the column. */
function Entry({
  item,
  index,
  canManage,
  overtimeTypes,
  onHours,
  onPatch,
  onDelete,
  onClear,
  columnLabel,
}: {
  readonly item: ResourceItem;
  readonly index: number;
  readonly columnLabel: string;
  readonly canManage: boolean;
  readonly overtimeTypes: readonly { id: string; name: string; multiplier: string }[];
  readonly onHours: (item: ResourceItem, hours: number) => void;
  readonly onPatch: (
    item: ResourceItem,
    patch: {
      overtimeTypeId?: string | null;
      distribution?: 'even' | 'earliest';
      booking?: 'hard' | 'soft';
      /** 27 Oct 2026 · the column a distribution change re-spreads (`patchAllocationRequestSchema`). */
      fromDate?: string;
      toDate?: string;
    },
  ) => void;
  readonly onDelete: (item: ResourceItem) => void;
  /** 27 Oct 2026 · empty THIS column of a booking, leaving its other weeks alone. */
  readonly onClear: (item: ResourceItem) => void;
}) {
  const t = useT();
  const hours = Number(item.cells[index] ?? '0');
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (raw: string): void => {
    setDraft(null);
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? 0 : parseHours(trimmed);
    if (parsed === null || parsed === hours) return;
    onHours(item, parsed);
  };

  const soft = item.booking === 'soft';
  const earliest = item.distribution === 'earliest';

  return (
    <div className="flex items-center gap-1">
      {/**
       * ⚠️⚠️ **AN INLINE EDITOR, NOT A FORM FIELD — SO IT CARRIES NO BOX UNTIL IT IS ASKED TO.**
       * `TextInput` draws `border-input` always, and `field.tsx` says why: *“the border is the
       * input's only identification”*. That is right for a form, where a reader has to find the
       * fields; it is wrong for a figure that is mostly READ, where a permanent box around every
       * number turns a pop-over into a form nobody asked to fill in.
       *
       * ⚠️ **The same treatment as `RailMoney`**, down to the class list: transparent at rest, the
       * input border on hover, and the full focus ring when it has the caret — so the affordance
       * arrives exactly when somebody reaches for it, and the product has one inline editor rather
       * than two that look different.
       */}
      <input
        type="text"
        disabled={!canManage}
        /**
         * ⚠️⚠️ **`0:00` AT REST, EMPTY ONCE IT HAS THE CARET.** A blank box in a column of
         * boxes reads as broken rather than as empty — reported on a freshly added booking, where
         * every OTHER row showed a figure and the new one showed nothing at all. Showing the zero
         * costs the property the blank was there for (the first keystroke replacing the cell
         * rather than appending to a `0`), so focus clears it instead: the figure is there to be
         * read, and gone the moment somebody types.
         */
        value={draft ?? formatHours(hours)}
        onFocus={() => {
          setDraft(hours === 0 ? '' : formatHours(hours));
        }}
        aria-label={t('resource.cell.hours', { name: item.lineItemName })}
        className="focus:border-input focus:bg-background hover:enabled:border-input h-7 w-[78px] min-w-0 rounded-md border border-transparent bg-transparent px-2 text-right text-[12px] tabular-nums outline-none disabled:cursor-default"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={(event) => {
          commit(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            setDraft(null);
          }
        }}
      />

      <div className="flex-1" />

      {/* ⚠️ Planned overtime — a property of the booking, and the pink is the grid's own dot. */}
      <DropdownMenu
        width={260}
        align="end"
        label={t('resource.item.overtimeLabel')}
        items={[
          {
            label: t('resource.cell.plain'),
            checked: item.overtimeTypeId === null,
            onSelect: () => {
              onPatch(item, { overtimeTypeId: null });
            },
          },
          ...overtimeTypes.map((type) => ({
            label: t('resource.item.overtime', { name: type.name, multiplier: type.multiplier }),
            checked: item.overtimeTypeId === type.id,
            onSelect: () => {
              onPatch(item, { overtimeTypeId: type.id });
            },
          })),
        ]}
      >
        <IconButton
          disabled={!canManage}
          name="timer"
          tone={item.overtimeTypeId === null ? 'muted' : 'pink'}
          hint={t('resource.item.overtimeLabel')}
          label={
            item.overtimeTypeName === null
              ? t('resource.cell.plain')
              : t('resource.item.overtime', {
                  name: item.overtimeTypeName,
                  multiplier: item.multiplier,
                })
          }
        />
      </DropdownMenu>

      {/* ⚠️ Daily priority — how a total lands across the days of a span. */}
      <DropdownMenu
        width={260}
        align="end"
        label={t('resource.cell.priority')}
        items={[
          {
            label: t('resource.dist.even'),
            checked: !earliest,
            onSelect: () => {
              onPatch(item, { distribution: 'even' });
            },
          },
          {
            label: t('resource.dist.earliest'),
            checked: earliest,
            onSelect: () => {
              onPatch(item, { distribution: 'earliest' });
            },
          },
        ]}
      >
        <IconButton
          disabled={!canManage}
          name="sliders-horizontal"
          tone={earliest ? 'primary' : 'muted'}
          hint={t('resource.cell.priority')}
          label={t('resource.cell.priorityIs', {
            mode: earliest ? t('resource.dist.earliest') : t('resource.dist.even'),
          })}
        />
      </DropdownMenu>

      {/* ⚠️ Hard or soft. See `ALLOCATION_BOOKINGS` for what the distinction does and does not do. */}
      <DropdownMenu
        width={280}
        align="end"
        label={t('resource.cell.booking')}
        items={[
          {
            label: t('resource.cell.hard'),
            description: t('resource.cell.hardHint'),
            icon: 'shield-check' as const,
            checked: !soft,
            onSelect: () => {
              onPatch(item, { booking: 'hard' });
            },
          },
          {
            label: t('resource.cell.soft'),
            description: t('resource.cell.softHint'),
            icon: 'square-dashed' as const,
            checked: soft,
            onSelect: () => {
              onPatch(item, { booking: 'soft' });
            },
          },
        ]}
      >
        <IconButton
          disabled={!canManage}
          name={soft ? 'square-dashed' : 'shield-check'}
          tone={soft ? 'muted' : 'primary'}
          hint={t('resource.cell.booking')}
          label={t('resource.cell.bookingIs', {
            kind: soft ? t('resource.cell.soft') : t('resource.cell.hard'),
          })}
        />
      </DropdownMenu>

      {/**
       * ⚠️ **A DRAFT IS DELETABLE TOO, AND IT IS NOT A DELETE.** There is nothing on the server to
       * remove — a booking with no hours cannot exist — but a row somebody added by mistake has to
       * be dismissable, and a disabled bin in a row that looks exactly like the others reads as a
       * bug rather than as a rule. The caller tells the two apart by the missing id.
       */}
      {/**
       * ⚠️⚠️ 27 Oct 2026 · **THE BIN ASKS WHICH.** It deleted the WHOLE allocation from a pop-over
       * that is about one column, so clearing a week took every other week with it. A saved booking
       * now offers both, this column first; a draft has nothing saved and is simply dropped.
       */}
      {item.allocationId === '' ? (
        <IconButton
          disabled={!canManage}
          name="trash"
          tone="muted"
          hint={t('resource.item.remove')}
          label={t('resource.item.remove')}
          onClick={() => {
            onDelete(item);
          }}
        />
      ) : (
        <DropdownMenu
          width={260}
          align="end"
          label={t('resource.cell.removeMenu')}
          items={[
            {
              label: t('resource.cell.clear', { column: columnLabel }),
              onSelect: () => {
                onClear(item);
              },
            },
            { type: 'separator' as const },
            {
              label: t('resource.cell.removeWhole'),
              destructive: true,
              onSelect: () => {
                onDelete(item);
              },
            },
          ]}
        >
          <IconButton
            disabled={!canManage}
            name="trash"
            tone="muted"
            hint={t('resource.cell.removeMenu')}
            label={t('resource.cell.removeMenu')}
          />
        </DropdownMenu>
      )}
    </div>
  );
}

function IconButton({
  name,
  tone,
  label,
  hint,
  disabled,
  onClick,
}: {
  readonly name: 'timer' | 'sliders-horizontal' | 'shield-check' | 'square-dashed' | 'trash';
  readonly tone: 'muted' | 'primary' | 'pink';
  /** The current VALUE — *Sima munkaidő*, *Fix*, *Egyenletes*. */
  readonly label: string;
  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHAT THE CONTROL IS — round 5 item 4.                                   │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THERE WAS ALREADY A `title`, AND IT SAID THE WRONG THING.** These four buttons
   * carried their VALUE — hovering the overtime one read *Sima munkaidő*, which says what the
   * setting is and not that the button sets overtime. *"All need a tooltip on hover so the user
   * knows what it's about."*
   *
   * ⚠️ **Both facts, in that order**: the control's name, then its value under it. A tooltip
   * that replaced the value with the name would answer the new question by losing the old answer.
   */
  readonly hint: string;
  readonly disabled: boolean;
  readonly onClick?: () => void;
}) {
  const colour =
    tone === 'pink'
      ? 'text-pink-600'
      : tone === 'primary'
        ? 'text-primary'
        : 'text-muted-foreground';
  return (
    /**
     * ⚠️⚠️ **THE KIT'S `Tooltip`, NOT THE BROWSER'S `title`.** A native tooltip waits about a
     * second, renders in the operating system's font and cannot hold two lines — and on a control
     * whose whole problem was that nobody knew what it did, the delay IS the problem.
     *
     * ⚠️ **`aria-label` carries both facts too**, because a screen reader never sees a tooltip:
     * what a sighted reader now gets on hover is what a non-sighted one gets from the button.
     */
    <Tooltip
      width={220}
      content={
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">{hint}</span>
          <span className="text-muted-foreground">{label}</span>
        </span>
      }
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={`${hint} · ${label}`}
        className={`hover:bg-accent hover:text-foreground flex size-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${colour}`}
        onClick={onClick}
      >
        <Icon name={name} size={13} />
      </button>
    </Tooltip>
  );
}
